import { useParams, useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useLote,
  useLaudos,
  useFaturas,
  useEmpresa,
  useConfiguracoes,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  Lock,
  Unlock,
  Receipt,
  AlertCircle,
  Car,
} from "lucide-react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import {
  MONTH_NAMES,
  LAUDO_STATUS_LABELS,
  LAUDO_STATUS_COLORS,
  VEICULO_TIPO_LABELS,
  FINANCEIRO_STATUS_LABELS,
  FINANCEIRO_STATUS_COLORS,
} from "@/lib/constants";

function formatBRL(value: string | number | null | undefined) {
  if (value == null) return "—";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export default function LoteDetalhes() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const isOwner = profile?.roles?.includes("owner") ?? false;
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const canClose = isOwner || isAdmin;

  const [showFecharDialog, setShowFecharDialog] = useState(false);
  const [ordemServico, setOrdemServico] = useState("");

  const { data: lote, isLoading: loadingLote } = useLote(id);
  const { data: laudos, isLoading: loadingLaudos } = useLaudos(
    lote?.vistoriadoraId,
    "finalizado",
    lote?.mes,
    lote?.ano,
  );
  const { data: vistoriadora } = useEmpresa(lote?.vistoriadoraId);
  const { data: faturas, isLoading: loadingFatura } = useFaturas(
    undefined,
    undefined,
    undefined,
    undefined,
    id,
  );
  const { data: configuracoes } = useConfiguracoes();

  const fecharLoteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(api.lotes.fechar.path.replace(":id", id!), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          ordemServico: ordemServico.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao fechar lote");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.lotes.get.path, id] });
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.faturas.list.path],
      });
      toast({ title: "Lote fechado com sucesso!" });
      setShowFecharDialog(false);
      setOrdemServico("");
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const fatura = faturas?.[0] ?? null;

  if (loadingLote) {
    return (
      <Shell>
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </Shell>
    );
  }

  if (!lote) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <AlertCircle className="w-10 h-10 text-slate-300" />
          <p>Lote não encontrado</p>
          <Button variant="outline" onClick={() => navigate("/lotes")}>
            Voltar
          </Button>
        </div>
      </Shell>
    );
  }

  const titulo = `${MONTH_NAMES[lote.mes - 1]} ${lote.ano}`;

  const showFecharBtn =
    canClose && !lote.fechado && configuracoes?.fechamentoAutomatico === false;

  return (
    <Shell>
      {/* Dialog: Fechar Lote */}
      <Dialog open={showFecharDialog} onOpenChange={setShowFecharDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fechar Lote — {titulo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              O fechamento irá gerar uma fatura com vencimento em 3 dias úteis.
            </p>
            <div className="space-y-1">
              <Label htmlFor="ordem-servico">
                Ordem de Serviço{" "}
                <span className="text-xs text-muted-foreground">
                  (opcional)
                </span>
              </Label>
              <Input
                id="ordem-servico"
                placeholder="Ex: OS-2026-001"
                value={ordemServico}
                onChange={(e) => setOrdemServico(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFecharDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => fecharLoteMutation.mutate()}
              disabled={fecharLoteMutation.isPending}
            >
              {fecharLoteMutation.isPending
                ? "Fechando..."
                : "Confirmar fechamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-muted-foreground"
          onClick={() => navigate("/lotes")}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar
        </Button>

        <div className="flex items-center gap-3 flex-wrap">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${lote.fechado ? "bg-green-100" : "bg-amber-100"}`}
          >
            {lote.fechado ? (
              <Lock className="w-5 h-5 text-green-600" />
            ) : (
              <Unlock className="w-5 h-5 text-amber-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold font-display text-slate-900">
            Lote — {titulo}
          </h1>
          <Badge
            variant="secondary"
            className={
              lote.fechado
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }
          >
            {lote.fechado ? "Fechado" : "Aberto"}
          </Badge>
          {showFecharBtn && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => setShowFecharDialog(true)}
            >
              <Lock className="w-4 h-4 mr-1.5" />
              Fechar Lote
            </Button>
          )}
        </div>

        {isOwner && lote.vistoriadoraNome && (
          <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
            <Building2 className="w-3.5 h-3.5" />
            {lote.vistoriadoraNome}
          </p>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 mb-6">
        {/* Resumo */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Resumo do Lote
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                Total de laudos
              </span>
              <span className="font-semibold">{lote.qtdLaudos}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Criado em
              </span>
              <span>{new Date(lote.criadoEm).toLocaleDateString("pt-BR")}</span>
            </div>
            {lote.fechamentoPrevisto && !lote.fechado && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Fechamento previsto
                </span>
                <span className="text-amber-600 font-medium">
                  {new Date(lote.fechamentoPrevisto).toLocaleDateString(
                    "pt-BR",
                  )}
                </span>
              </div>
            )}
            {lote.fechadoEm && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" />
                  Fechado em
                </span>
                <span>
                  {new Date(lote.fechadoEm).toLocaleDateString("pt-BR")}
                </span>
              </div>
            )}
            {lote.ordemServico && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Ordem de Serviço</span>
                <span className="font-mono font-medium">
                  {lote.ordemServico}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fatura */}
        <Card className="border-none shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Receipt className="w-3.5 h-3.5" />
              Fatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingFatura ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : fatura ? (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total</span>
                  <span className="font-bold text-base text-slate-900">
                    {formatBRL(fatura.valorTotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-slate-500">Status</span>
                  <Badge
                    variant="secondary"
                    className={
                      FINANCEIRO_STATUS_COLORS[fatura.status ?? ""] ?? ""
                    }
                  >
                    {FINANCEIRO_STATUS_LABELS[fatura.status ?? ""] ??
                      fatura.status}
                  </Badge>
                </div>
                {fatura.vencimento && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Vencimento</span>
                    <span>
                      {new Date(fatura.vencimento).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                )}
                {fatura.pagoEm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Pago em</span>
                    <span>
                      {new Date(fatura.pagoEm).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Sem fatura gerada para este lote
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resumo por Tipo de Veículo */}
      {(() => {
        if (loadingLaudos) {
          return (
            <Card className="border-none shadow-md mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Car className="w-3.5 h-3.5" />
                  Resumo por Tipo de Veículo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          );
        }

        if (!laudos || laudos.length === 0) return null;

        const valoresPorTipo = vistoriadora?.valoresPorTipo as
          | Record<string, string>
          | null
          | undefined;
        const valorFlat = vistoriadora?.valorPorLaudo
          ? Number(vistoriadora.valorPorLaudo)
          : null;
        const temPrecificacao = valoresPorTipo || valorFlat;

        const contagens: Record<string, number> = {};
        for (const laudo of laudos) {
          const tipo = laudo.veiculo?.tipo ?? "desconhecido";
          contagens[tipo] = (contagens[tipo] ?? 0) + 1;
        }

        let totalEstimado = 0;
        const linhas = Object.entries(contagens).map(([tipo, qtd]) => {
          let valorUnitario: number | null = null;
          if (valoresPorTipo && valoresPorTipo[tipo] != null) {
            valorUnitario = Number(valoresPorTipo[tipo]);
          } else if (valorFlat != null) {
            valorUnitario = valorFlat;
          }
          const subtotal = valorUnitario != null ? valorUnitario * qtd : null;
          if (subtotal != null) totalEstimado += subtotal;
          return { tipo, qtd, valorUnitario, subtotal };
        });

        return (
          <Card className="border-none shadow-md mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Car className="w-3.5 h-3.5" />
                Resumo por Tipo de Veículo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-slate-400 uppercase tracking-wide">
                    <th className="text-left pb-2 font-medium">Tipo</th>
                    <th className="text-right pb-2 font-medium">Qtd</th>
                    {temPrecificacao && (
                      <>
                        <th className="text-right pb-2 font-medium">
                          Valor unit.
                        </th>
                        <th className="text-right pb-2 font-medium">
                          Subtotal
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(({ tipo, qtd, valorUnitario, subtotal }) => (
                    <tr key={tipo} className="border-b last:border-0">
                      <td className="py-2 text-slate-700">
                        {VEICULO_TIPO_LABELS[tipo] ?? tipo}
                      </td>
                      <td className="py-2 text-right font-semibold">{qtd}</td>
                      {temPrecificacao && (
                        <>
                          <td className="py-2 text-right text-slate-500">
                            {valorUnitario != null
                              ? formatBRL(valorUnitario)
                              : "—"}
                          </td>
                          <td className="py-2 text-right font-semibold">
                            {subtotal != null ? formatBRL(subtotal) : "—"}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                {temPrecificacao && linhas.length > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-200">
                      <td
                        colSpan={2}
                        className="pt-2 text-slate-500 text-xs font-medium uppercase tracking-wide"
                      >
                        Total estimado
                      </td>
                      <td />
                      <td className="pt-2 text-right font-bold text-base text-slate-900">
                        {formatBRL(totalEstimado)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
              {!temPrecificacao && (
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  Configure os valores por tipo na empresa para ver o total
                  estimado
                </p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Laudos */}
      <Card className="border-none shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Laudos finalizados neste lote
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingLaudos ? (
            <div className="p-4 space-y-3">
              {Array(4)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
            </div>
          ) : laudos && laudos.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3 font-medium">Laudo</th>
                    <th className="text-left px-4 py-3 font-medium">
                      Parceiro
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Veículo</th>
                    <th className="text-left px-4 py-3 font-medium">Tipo</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">
                      Data Vistoria
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {laudos.map((laudo, idx) => (
                    <tr
                      key={laudo.id}
                      className={`border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors ${idx % 2 === 0 ? "" : "bg-slate-50/50"}`}
                      onClick={() => navigate(`/laudos/${laudo.id}`)}
                    >
                      <td className="px-4 py-3 font-mono font-medium text-blue-600">
                        {laudo.laudoId ?? laudo.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-sm">
                        {laudo.parceiro
                          ? (laudo.parceiro.nomeFantasia ??
                            laudo.parceiro.razaoSocial ??
                            "—")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {laudo.veiculo
                          ? `${laudo.veiculo.marca} ${laudo.veiculo.modelo}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {laudo.veiculo?.tipo
                          ? (VEICULO_TIPO_LABELS[laudo.veiculo.tipo] ??
                            laudo.veiculo.tipo)
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${LAUDO_STATUS_COLORS[laudo.status] ?? ""}`}
                        >
                          {LAUDO_STATUS_LABELS[laudo.status] ?? laudo.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {laudo.dataVistoria
                          ? new Date(laudo.dataVistoria).toLocaleDateString(
                              "pt-BR",
                            )
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">
                Nenhum laudo finalizado encontrado para {titulo}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
}
